import * as React from 'react';

import type { PrintJobInfo } from '@rematter/frontend-lib-api-client/grpc/sidecar/v1/printer_local_api_pb';
import { Button, Row } from '@rematter/ui-web';
import { LucideRefreshCw, LucideTestTubeDiagonal, LucideX } from 'lucide-react';

import { useTranslations } from '../../../i18n/useTranslations';
import type { PrinterDetailRecord } from '../../../types/sidecar';

import type { SidecarConnection } from '~/shared/providers/libp2p/types';
import { usePrinterLocalClient } from '~/shared/providers/libp2p/usePrinterLocalClient';

type Props = {
  printer: PrinterDetailRecord;
  sidecarId: string;
  connection: SidecarConnection | undefined;
};

export const PrinterTabQueue: React.FC<Props> = ({
  printer,
  sidecarId,
  connection,
}) => {
  const t = useTranslations();
  const printerClient = usePrinterLocalClient(sidecarId);

  const [jobs, setJobs] = React.useState<PrintJobInfo[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const isConnected = connection?.connected ?? false;

  const fetchQueue = React.useCallback(async () => {
    if (!printerClient || !printer.id) return;
    setIsLoading(true);
    setError(null);

    try {
      const response = await printerClient.getPrintQueue({
        printerId: printer.id,
      });
      setJobs(response.jobs);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  }, [printerClient, printer.id]);

  React.useEffect(() => {
    if (!isConnected || !printerClient || !printer.id) return;

    let cancelled = false;

    const subscribe = async () => {
      try {
        for await (const response of printerClient.subscribePrintQueue({
          printerId: printer.id,
        })) {
          if (cancelled) break;
          setJobs(response.jobs);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    };

    setIsLoading(true);
    subscribe();

    return () => {
      cancelled = true;
    };
  }, [isConnected, printerClient, printer.id]);

  const handlePrintTestPage = React.useCallback(async () => {
    if (!printerClient) return;
    setError(null);

    try {
      await printerClient.printTestPage({
        printerId: printer.id,
        printerType: printer.printerType ?? '',
        pageSize: printer.pageSize ?? '',
        rollWidth: printer.rollWidth ?? '',
      });
      fetchQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [printerClient, printer, fetchQueue]);

  const handleCancelJob = React.useCallback(
    async (jobId: number) => {
      if (!printerClient) return;
      setError(null);

      try {
        await printerClient.cancelPrintJob({
          printerId: printer.id,
          jobId,
        });
        fetchQueue();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [printerClient, printer.id, fetchQueue],
  );

  if (!isConnected) {
    return (
      <div className="@PrinterTabQueue flex max-h-full flex-col gap-4 p-4">
        <div className="text-sm text-neutral/60">
          {t.hardwareView.printerDetailView.messages.notConnected}
        </div>
      </div>
    );
  }

  return (
    <div className="@PrinterTabQueue flex max-h-full flex-col gap-4 p-4">
      <Row gap2 className="flex-wrap items-end">
        <Button variant="outline" onPress={fetchQueue} isDisabled={isLoading}>
          <LucideRefreshCw className="mr-1 size-4" />
          {t.hardwareView.printerDetailView.actions.refresh}
        </Button>
        <Button onPress={handlePrintTestPage} isDisabled={!printerClient}>
          <LucideTestTubeDiagonal className="mr-1 size-4" />
          {t.hardwareView.printerDetailView.actions.printTestPage}
        </Button>
      </Row>

      {error && (
        <div className="rounded bg-failure/10 p-2 text-sm text-failure">
          {error}
        </div>
      )}

      {jobs.length === 0 ? (
        <div className="text-sm text-neutral/60">
          {t.hardwareView.printerDetailView.messages.queueEmpty}
        </div>
      ) : (
        <div className="overflow-auto rounded border border-neutral/20">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral/20 bg-neutral/5">
                <th className="px-3 py-2 text-left font-medium text-neutral/60">
                  {t.hardwareView.printerDetailView.queueColumns.documentName}
                </th>
                <th className="px-3 py-2 text-left font-medium text-neutral/60">
                  {t.hardwareView.printerDetailView.queueColumns.status}
                </th>
                <th className="px-3 py-2 text-left font-medium text-neutral/60">
                  {t.hardwareView.printerDetailView.queueColumns.pages}
                </th>
                <th className="px-3 py-2 text-left font-medium text-neutral/60">
                  {t.hardwareView.printerDetailView.queueColumns.size}
                </th>
                <th className="px-3 py-2 text-left font-medium text-neutral/60" />
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.id} className="border-b border-neutral/10">
                  <td className="px-3 py-2">{job.documentName}</td>
                  <td className="px-3 py-2">{job.status}</td>
                  <td className="px-3 py-2">{job.pages}</td>
                  <td className="px-3 py-2">{job.size}</td>
                  <td className="px-3 py-2">
                    <Button
                      variant="outline"
                      size="small"
                      onPress={() => handleCancelJob(job.id)}
                    >
                      <LucideX className="mr-1 size-3" />
                      {t.hardwareView.printerDetailView.actions.cancelJob}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
